/**
 * INVESTIGATION (Firmflow admin):
 * - Login page: `src/pages/LoginPage.js` — "Forgot password?" uses `<MuiLink component={Link} to="/forgot-password">`
 *   (React Router; not a bare `<a href>`).
 * - Prior bug: this component called POST `/api/auth/password-reset/` (nonexistent vs current API) and surfaced
 *   "Email not found" on failure — violating the requirement to never disclose whether an email exists.
 * - Routes `/forgot-password` and `/reset-password/:token` are registered as public routes in `App.js` (no ProtectedRoute).
 */
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  InputAdornment,
  Link as MuiLink,
} from '@mui/material';
import { Email, ArrowBack } from '@mui/icons-material';
import { requestPasswordReset } from '../api/auth';
import { tokens } from '../theme/theme';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    const trimmed = email.trim();
    if (!trimmed) {
      setEmailError('Email is required');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setEmailError('Enter a valid email address');
      return;
    }
    setEmailError('');
    setSubmitting(true);
    try {
      const result = await requestPasswordReset(trimmed, false);
      if (result.ok) {
        setSuccess(true);
      } else if (result.rateLimited) {
        setSubmitError(result.message);
      } else {
        setSubmitError(result.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        py: 4,
        px: 2,
      }}
    >
      <Box sx={{ mx: 'auto', maxWidth: 400, width: '100%' }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/')}
            sx={{ color: 'text.secondary' }}
          >
            Back to Home
          </Button>
        </Box>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
            Reset your password
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
            Enter your email and we&apos;ll send you a link to reset your password.
          </Typography>
        </Box>
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            bgcolor: 'background.paper',
            p: 4,
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            boxShadow: tokens.shadow.sm,
          }}
        >
          {success ? (
            <Alert severity="success">
              If an account with that email exists, you&apos;ll receive an email shortly. Check your inbox.
            </Alert>
          ) : null}
          {!success && submitError ? (
            <Alert severity="error">{submitError}</Alert>
          ) : null}
          {!success ? (
            <>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (emailError) setEmailError('');
                  if (submitError) setSubmitError('');
                }}
                error={!!emailError}
                helperText={emailError}
                size="small"
                autoComplete="email"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email sx={{ color: 'text.secondary', mr: 0.5 }} />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={submitting}
                sx={{ py: 1.5, textTransform: 'none', fontWeight: 600, borderRadius: 1.5 }}
              >
                {submitting ? 'Sending…' : 'Send reset link'}
              </Button>
            </>
          ) : null}
          <Box sx={{ textAlign: 'center' }}>
            <MuiLink component={Link} to="/login" variant="body2" sx={{ fontWeight: 500 }}>
              Back to login
            </MuiLink>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ForgotPassword;
